import React, { useState, useMemo, useEffect } from "react"
import { Button, Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import { FaPlus, FaPen, FaSearch, FaEye } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import Swal from "sweetalert2"
import ServiceModal from "./ServiceModal"
import Breadcrumb from "components/Common/Breadcrumb"
import { FaX } from "react-icons/fa6"
import serviceIMG from "../../assets/images/serviceside.jpeg"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import moment from "moment"
import { FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const ServicesIndex = () => {
  const [services, setServices] = useState([])

  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [modalType, setModalType] = useState("")

  const [serviceData, setServiceData] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const toggleModal = () => setIsModalOpen(!isModalOpen)

  useEffect(() => {
    getServicesData()
  }, [])

  async function getServicesData() {
    const APIdata = await fetchData(setLoading, "services")
    if (APIdata?.success) {
      setServices(APIdata?.data.reverse())
    } else {
      Swal.fire({
        title: "Error Fetching Services",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setServiceData(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async e => {
    e.preventDefault()
    if (serviceData.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "services",
        serviceData?.id,
        serviceData
      )
      if (APIdata?.data.success) {
        Swal.fire({
          title: "Service Updated Successfully.",
          icon: "success",
        })
        getServicesData()
      } else {
        Swal.fire({
          title: "Something Went Wrong",
          text: APIdata?.response?.data?.message || APIdata?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "services", serviceData)
      if (APIdata?.data) {
        Swal.fire({
          title: "Service Added Successfully.",
          icon: "success",
        })
        getServicesData()
      } else {
        Swal.fire({
          title: "Something Went Wrong",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
    toggleModal()
  }

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Service",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3d6eb6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Delete it",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIData = await FetchDeleteData(setLoading, "services", id)
        if (APIData?.data?.success) {
          Swal.fire({
            title: "Service Deleted Successfully.",
            icon: "success",
          })
          getServicesData()
        } else {
          Swal.fire({
            title: "Something Went Wrong",
            text: APIData?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  // 🔎 Filtering
  const filteredServices = useMemo(() => {
    return services.filter(
      srv =>
        srv.name.toLowerCase().includes(searchString.toLowerCase()) ||
        srv.description.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [services, searchString])

  // 📄 Pagination logic
  const indexOfLastItem = currentPage * perPageItem
  const indexOfFirstItem = indexOfLastItem - perPageItem
  const currentServices = filteredServices.slice(
    indexOfFirstItem,
    indexOfLastItem
  )
  const totalPages = Math.ceil(filteredServices.length / perPageItem)

  const { checkAccess } = usePermissionData()

  return (
    <div className="page-content">
      <PermittedComponent isAccess={checkAccess(9, 1) || checkAccess(9, 2)}>
        <Container fluid>
          {loading && <Loader />}
          <Breadcrumb
            title="Dashboard"
            breadcrumbItem="Services"
            description="Manage the Services for Locations from Here."
          />
          <Row>
            <Col lg="4">
              <img
                src={serviceIMG}
                className="w-100 h-100 object-fit-cover shadow-lg"
              />
            </Col>
            <Col lg="8" className="px-0">
              <Card className="mb-0 shadow-lg">
                <CardBody>
                  {/* Header Controls */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-3">
                      <select
                        className="form-select rounded-4 ps-3 pe-5 w-auto"
                        value={perPageItem}
                        onChange={e => {
                          setPerPageItem(Number(e.target.value))
                          setCurrentPage(1)
                        }}
                      >
                        <option value={10}>Show 10</option>
                        <option value={20}>Show 20</option>
                        <option value={50}>Show 50</option>
                      </select>

                      <div className="position-relative">
                        <input
                          className="form-control rounded-4 px-3 pe-5"
                          placeholder="Search Service"
                          value={searchString}
                          onChange={e => {
                            setSearchString(e.target.value)
                            setCurrentPage(1)
                          }}
                        />
                        <span className="position-absolute end-0 top-50 translate-middle-y pe-3 text-muted">
                          <FaSearch />
                        </span>
                      </div>
                    </div>
                    {checkAccess(9, 3) && (
                      <div className="d-flex gap-3">
                        <Button
                          className="btn-blue"
                          onClick={() => {
                            setServiceData({})
                            toggleModal()
                            setModalType("")
                          }}
                        >
                          <FaPlus className="me-2" /> Service
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Services Table */}
                  <div className="mt-5">
                    <Table responsive hover>
                      <thead className="bg-blue text-white">
                        <tr>
                          <th>#</th>
                          <th>Services</th>
                          <th className="">Service Type</th>
                          <th className="text-center">Assigned Locations</th>
                          <th className="text-center">Created At</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentServices.length > 0 ? (
                          currentServices.map((srv, index) => (
                            <tr key={srv.id}>
                              <td>{indexOfFirstItem + index + 1}</td>
                              <td>{srv.name}</td>
                              <td>{srv.type === "window_cleaning" ? "Window Cleaning" : "Indutrial Cleaning"}</td>
                              <td className="text-center">
                                {srv?.locationCount}
                              </td>
                              <td className="text-center">
                                {moment(srv?.createdAt).format(
                                  "DD/MM/YYYY | hh:mm A"
                                )}
                              </td>
                              <td className="d-flex justify-content-center">
                                <Button
                                  size="sm"
                                  className="me-2 btn-blue"
                                  onClick={() => {
                                    setServiceData(srv)
                                    toggleModal()
                                    setModalType("view")
                                  }}
                                >
                                  <FaEye size={14} />
                                </Button>
                                {checkAccess(9, 4) && (
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => {
                                      setServiceData(srv)
                                      toggleModal()
                                      setModalType("")
                                    }}
                                  >
                                    <FaPen size={14} />
                                  </Button>
                                )}
                                {checkAccess(9, 5) && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(srv.id)}
                                  >
                                    <MdDelete size={14} />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              No service found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between  gap-2">
                    <p className="mb-0">
                      Showing {currentServices.length} out of {services.length}{" "}
                      Services.
                    </p>
                    <div>
                      <Button
                        size="sm"
                        disabled={currentPage === 1}
                        className="fs-6"
                        onClick={() => setCurrentPage(prev => prev - 1)}
                      >
                        <FaChevronCircleLeft />
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          className="mx-2 fs-6"
                          color={currentPage === i + 1 ? "primary" : "light"}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        className="fs-6"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                      >
                        <FaChevronCircleRight />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal */}
        <ServiceModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          serviceData={serviceData}
          onChange={handleChange}
          onSave={handleSave}
          type={modalType}
        />
      </PermittedComponent>
    </div>
  )
}

export default ServicesIndex
